/**
 * Date field over the gallery's already-installed datepicker.
 *
 * Range mode is two `asSingle` pickers (start + end) so a half-open
 * `{ startDate, endDate: null }` is still reachable — the range-mode picker
 * only commits a complete pair. Single mode uses `asSingle` + `useRange={false}`.
 * `inputId` keeps labels addressable (`${name}_start_date` / `${name}_date`).
 * Display format drops the ordinal (`Do` → `D`) because the picker's own
 * dayjs has no `advancedFormat` plugin.
 */

import React, { useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import dayjs from "dayjs";

import { Wrapper, Label } from "./styledComponents";

const TODAY_CONFIRM =
  "You are about to activate this channel now. You won't be able to edit the event date after you activate the channel. Are you sure you want to activate the channel for today? Click cancel to change your event date";

const RANGE_INPUT_CLASS =
  "relative w-full h-10 pl-4 pr-10 border border-mb-light-gray rounded text-sm font-normal text-mb-medium-gray bg-mb-white cursor-pointer focus:border-mb-main focus:outline-none focus:ring-0";
const SINGLE_INPUT_CLASS =
  "relative w-full h-12 pl-5 pr-14 border border-mb-light-gray rounded text-xs leading-[47px] text-mb-body bg-mb-white cursor-pointer focus:border-mb-main focus:outline-none focus:ring-0";
const TOGGLE_CLASS =
  "absolute right-0 h-full px-3 text-mb-menu-link focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed";

const confirmDate = (date) => {
  if (!date) return;
  if (dayjs(date).startOf("day").isSame(dayjs().startOf("day"))) {
    confirm(TODAY_CONFIRM);
  }
};

// Test-only bridge so refetchScope can drive the datepicker's onChange shape
// (`{ startDate, endDate }`) through the form without clicking the calendar.
let testOnChange = null;
export const emitDateChangeForTests = (value) => {
  if (typeof testOnChange !== "function") {
    throw new Error("Date field is not mounted");
  }
  testOnChange(value);
};

const toPickerDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const toPickerValue = (value) => {
  const date = toPickerDate(value);
  return { startDate: date, endDate: date };
};

const DateField = ({
  labelClassName = "",
  label,
  placeholder,
  required,
  disabled,
  containerClass,
  isDateRangeMode,
  field,
  value,
  date,
  onChange,
}) => {
  const [focused, setFocused] = useState(false);
  const fieldName = (field && field.name) || "date";
  const startDateId = `${fieldName}_start_date`;
  const endDateId = `${fieldName}_end_date`;
  const singleDateId = `${fieldName}_date`;
  const labelFor = isDateRangeMode ? startDateId : singleDateId;

  const start = toPickerDate(
    isDateRangeMode ? value && value.startDate : date ?? value
  );
  const end = toPickerDate(
    isDateRangeMode ? value && value.endDate : date ?? value
  );

  const handleRangeStart = (next) => {
    const newStart = next && next.startDate != null ? next.startDate : null;
    const nextEnd =
      newStart && end && dayjs(end).isBefore(dayjs(newStart), "day")
        ? newStart
        : end;
    onChange({ startDate: newStart, endDate: nextEnd });
  };

  const handleRangeEnd = (next) => {
    const newEnd = next && next.startDate != null ? next.startDate : null;
    const nextStart =
      newEnd && start && dayjs(newEnd).isBefore(dayjs(start), "day")
        ? newEnd
        : start;
    onChange({ startDate: nextStart, endDate: newEnd });
  };

  const handleSingle = (next) => {
    const picked = next && next.startDate != null ? next.startDate : null;
    onChange(picked);
    confirmDate(picked);
  };

  if (process.env.NODE_ENV === "test") {
    testOnChange = isDateRangeMode
      ? (next) => onChange(next)
      : handleSingle;
  }

  return (
    <Wrapper
      className={containerClass ? containerClass : ""}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      }}
    >
      {isDateRangeMode ? (
        <div className="flex w-full gap-2">
          <Datepicker
            value={toPickerValue(start)}
            onChange={handleRangeStart}
            asSingle
            useRange={false}
            inputId={startDateId}
            inputName={`${fieldName}_start`}
            displayFormat="MM/DD/YYYY"
            placeholder="Start"
            disabled={disabled}
            required={required}
            popoverDirection="down"
            primaryColor="pink"
            containerClassName="relative w-full text-mb-body"
            inputClassName={RANGE_INPUT_CLASS}
            toggleClassName={TOGGLE_CLASS}
          />
          <Datepicker
            value={toPickerValue(end)}
            onChange={handleRangeEnd}
            asSingle
            useRange={false}
            inputId={endDateId}
            inputName={`${fieldName}_end`}
            displayFormat="MM/DD/YYYY"
            placeholder="End"
            disabled={disabled}
            popoverDirection="down"
            primaryColor="pink"
            containerClassName="relative w-full text-mb-body"
            inputClassName={RANGE_INPUT_CLASS}
            toggleClassName={TOGGLE_CLASS}
          />
        </div>
      ) : (
        <Datepicker
          value={toPickerValue(start)}
          onChange={handleSingle}
          asSingle
          useRange={false}
          inputId={singleDateId}
          inputName={fieldName}
          displayFormat="dddd, MMMM D YYYY"
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          popoverDirection="down"
          primaryColor="pink"
          containerClassName="relative w-full text-mb-body"
          inputClassName={SINGLE_INPUT_CLASS}
          toggleClassName={TOGGLE_CLASS}
        />
      )}
      {label ? (
        <Label
          htmlFor={labelFor}
          className={`${labelClassName} date-label ${focused ? " focused " : ""}`}
        >
          {label}
        </Label>
      ) : null}
      {isDateRangeMode && (
        <label htmlFor={endDateId} className="sr-only">
          end date
        </label>
      )}
    </Wrapper>
  );
};

export default DateField;
